// OrderFlowMyMLMRequestList
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { logout } from '../../redux/actions/authActions';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import '../learning/styles/Training.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEye, faPenToSquare } from '@fortawesome/free-solid-svg-icons';

const OrderFlowMyMLMRequestList = () => {
    const authState = useSelector(state => state.authReducer);
    const [userName, setUserName] = useState(authState.user.name);    
    const [userId, setUserId] = useState(authState.user._id);
    const [myMLMRequests, setMyMLMRequests] = useState([]);
    const [fetchData, { loading }] = useFetch();
    const [selectedMLMRequestId, setSelectedMLMRequestId] = useState(null);
    const [showItineraryClicked, setShowItineraryClicked] = useState(false);
    const isLoggedIn = authState?.isLoggedIn || false;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        dispatch(logout());
        navigate("/orderflow-home");
    };

    const fetchMyMLMRequest = useCallback(() => {
        const config = { 
          url: "/mlmrequest/mymlmrequestlist", 
          method: "post", 
          headers: { Authorization: authState.token }, 
          data: {userId: userId}
        };
        // console.log("My MLM Request Config: ",config)
    
        fetchData(config, { showSuccessToast: false })
        .then(({ mlmrequest }) => setMyMLMRequests(mlmrequest))
        .catch(error => console.error("Error fetching data:", error));
    
    }, [authState.token, fetchData, userId]);
    
    useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchMyMLMRequest();
    }, [authState.isLoggedIn, fetchMyMLMRequest]);
  
    const sortedMyMLMRequests = [...myMLMRequests].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
     
    const handleMyMLMRequestView = (_id) => {
      setSelectedMLMRequestId(prevId => (prevId === _id ? null : _id));
    };
  
    const formatDateTime = (date) => {
      const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      };
      const jsDate = new Date(date);
      const day = jsDate.getDate().toString().padStart(2, '0');
      const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
      const year = jsDate.getFullYear();
      const hours = jsDate.getHours().toString().padStart(2, '0');
      const minutes = jsDate.getMinutes().toString().padStart(2, '0');
      const seconds = jsDate.getSeconds().toString().padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-900 text-gray-100">
            <header className="sticky top-0 z-50 bg-black border border-cyan-400 px-4 py-2 flex flex-row items-center justify-between shadow-lg">
                <div className='flex flex-col items-start justify-start'>
                    <h2 className='text-xl lg:text-2xl font-bold text-yellow-200 text-center mb-2'>
                        <i className="fas fa-shopping-cart text-xl lg:text-2xl text-yellow-200"></i>Khareedo
                    </h2>
                    <h2 className='text-sm font-bold text-white text-center mb-2'>
                        Request Products to Sell under MLM
                    </h2> 
                </div>  
                <div>
                <div className="hidden md:flex items-center gap-3">
                    {isLoggedIn ? (
                        <>
                            <div className="text-xs text-left text-blue-300">{userName}</div>
                            <button
                                onClick={handleLogout}
                                className="p-2 mt-2 rounded-md bg-blue-600 hover:bg-blue-700 transition"
                                title="Logout"
                            >
                                ⏻
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                className="px-3 py-1 rounded-full border border-blue-700 text-sm text-blue-200 hover:bg-blue-900"
                            >
                                Login
                            </Link>
                            <Link
                                to="/signup"
                                className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm hover:bg-blue-400"
                            >
                                Register
                            </Link>
                        </>
                    )}
                </div>
            
                {/* Mobile hamburger */}
                <button
                    onClick={() => setIsMobileMenuOpen((s) => !s)}
                    className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-blue-300 bg-blue-500"
                    aria-label="Toggle menu"
                >
                    {isMobileMenuOpen ? "×" : "☰"}
                </button>
            </div>
        </header>
            
        {/* Mobile menu content */}
        {isMobileMenuOpen && (
            <div className="md:hidden bg-black border-b border-cyan-400 px-4 py-2 animate-fadeIn">
                {isLoggedIn ? (
                    <div className="flex flex-row items-center justify-center gap-3">
                        <span className="text-blue-300 font-medium whitespace-nowrap h-7">
                            Welcome, {userName}
                        </span>
                        <button
                            onClick={handleLogout}
                            className="rounded-md bg-blue-600 hover:bg-blue-700 w-7 h-7 flex items-center justify-center text-white"
                            title="Logout"
                        >
                            ⏻
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-row items-center justify-center gap-6">
                        <Link to="/login" className="text-blue-300 hover:text-yellow-300">
                            Login
                        </Link>
                        <span className="text-blue-500">|</span>
                        <Link to="/signup" className="text-blue-300 hover:text-yellow-300">
                            Register
                        </Link>
                    </div>
                )}
            </div>
        )}  

            <main className="flex-1">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-300 mx-4 pb-4 mb-4 gap-2 sm:gap-0">
                    <div>
                    <h3 className='text-sm sm:text-base mt-2 mx-1 text-white'>
                            My MLM Product Registration Requests:  ({myMLMRequests.length})
                    </h3>
                    </div>
                    <div className='w-full sm:w-auto'>
                        <Link to='/mlmproductrequest/add' className='block w-full sm:inline-block px-4 py-2 mt-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm font-medium'>
                            <FontAwesomeIcon className="mr-2" icon={faPlus} />  Add New
                        </Link>
                    </div>
                </div>
                <div className="mt-4 px-4 space-y-3">
                    {loading ? (
                        <Loader className="mx-auto" />
                    ) : (
                    <div className="space-y-3">
                        {myMLMRequests.length === 0 ? (
                        <div className='flex justify-center py-8 px-4 text-white text-base bg-gray-800 rounded-lg shadow-md'>No request found!!</div>
                        ) : (
                            sortedMyMLMRequests.map((mlmrequest, index) => (
                                <div key={mlmrequest._id} className='bg-white p-3 sm:p-4 text-gray-600 rounded-lg shadow-md space-y-3'>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                                        <div className="flex-1 space-y-1 sm:space-y-0">
                                            <span className="font-medium text-gray-900 text-sm">{index + 1}.</span> 
                                            <span className="ml-2 text-gray-900 font-semibold text-sm sm:text-base">{mlmrequest.productName}</span>
                                        </div>
                                        <div className="flex items-center space-x-2 mt-2 sm:mt-0">
                                            <Tooltip text={"View details"} position={"top"}>
                                                <button 
                                                    className='text-blue-500 hover:text-blue-700 p-1 rounded transition-colors'
                                                    onClick={() => handleMyMLMRequestView(mlmrequest._id)}
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                </button>
                                            </Tooltip>
                                            <Tooltip text={"Edit this record"} position={"top"}>
                                                <Link 
                                                    to={`/mlmproductrequest/${mlmrequest._id}`} 
                                                    className='text-green-600 hover:text-green-800 p-1 rounded transition-colors'
                                                >
                                                    <FontAwesomeIcon icon={faPenToSquare} />
                                                </Link>
                                            </Tooltip>
                                        </div>
                                    </div>
                                    {selectedMLMRequestId === mlmrequest._id && (
                                        <div className='bg-gray-200 text-black p-3 rounded-md space-y-2 text-xs sm:text-sm'>
                                            <p className='text-green-500'><strong>Product Code:</strong> {mlmrequest.productCode}</p>
                                            <p className='text-lg text-blue-500 font-bold'>{mlmrequest.isApproved ? "Approved" : "In Review"}</p>
                                            <p><strong>Approval Date:</strong> {mlmrequest.approvalDate ? formatDateTime(mlmrequest.approvalDate) : "Not yet approved"}</p>
                                            <p><strong>Review / Approval Comment:</strong> {mlmrequest.reviewComment}</p>
                                            <p><strong>{mlmrequest.title}</strong></p>
                                            <p className='mb-2'><strong>Request Date:</strong> {formatDateTime(mlmrequest.dateCreated)}</p>
                                            <p className='mb-2'><strong>Contact Details:</strong> {mlmrequest.contactDetails}</p>
                                            <p><strong>Product Name:</strong> {mlmrequest.productName}</p>
                                            <p><strong>Product Features:</strong> {mlmrequest.productFeatures}</p>
                                            <p><strong>Product Description:</strong> {mlmrequest.productDesc}</p>
                                            <p><strong>Category:</strong> {mlmrequest.category}</p>
                                            <p className='mb-2'><strong>Sub-Category:</strong> {mlmrequest.subCategory}</p>
                                            <p><strong>Brand Name:</strong> {mlmrequest.brandName}</p>
                                            <p><strong>Seller Name:</strong> {mlmrequest.sellerName}</p>
                                            <p className='mb-2'><strong>Manufacturer Name:</strong> {mlmrequest.manufacturerName}</p>
                                            <p><strong>List Price:</strong> ₹{mlmrequest.listPrice}</p>
                                            <p><strong>Offer Price:</strong> ₹{mlmrequest.offerPrice}</p>
                                            <p><strong>Tax Structure:</strong> {mlmrequest.taxStructure}</p>
                                            <p><strong>Delivery Charges:</strong> ₹{mlmrequest.deliveryCharges}</p>
                                            <p className='mb-2'><strong>Currency:</strong> {mlmrequest.currency}</p>
                                            <p className='text-yellow-600'>{mlmrequest.isCombo ? "It's a Combo Product" : "It's a Solo Product"}</p>

                                            <p className='text-orange-500'><strong>Combo Name:</strong> {mlmrequest.isCombo ? mlmrequest.comboName : "N/A"}</p>

                                            <p className='mb-2'><strong>Combo Details:</strong> {mlmrequest.isCombo ? mlmrequest.comboDetails : "N/A"}</p>
                                            <p className='text-yellow-600'>{mlmrequest.isBOGO ? "It's a BOGO - Buy One Get One Offer" : "It's Single Offer"}</p>
                                            <p className='text-blue-600'>{mlmrequest.isSubscription ? "Subscription Product" : "Non-Subscription Product"}</p>
                                            <p className='text-orange-500 mb-2'>{mlmrequest.isRenewable ? "Renew / Repeat" : "One Time Buy"}</p>
                                            <p><strong>Is Second Hand:</strong> {mlmrequest.isSecondHand ? "Yes" : "No"}</p>
                                            <p><strong>Is Adult Product:</strong> {mlmrequest.isAdult ? "Yes" : "No"}</p>
                                            <p><strong>Is Children Product:</strong> {mlmrequest.isChildren ? "Yes" : "No"}</p>
                                            <p className='mb-2'><strong>Is Women's Product:</strong> {mlmrequest.isWomen ? "Yes" : "No"}</p>
                                            <p><strong>GST Registered:</strong> {mlmrequest.isGSTRegistered ? "Yes" : "No"}</p>
                                            <p className='mb-2'><strong>GST Number:</strong> {mlmrequest.gstNumber || "N/A"}</p>
                                            <p className='mb-2'><strong>Website:</strong> {mlmrequest.website}</p>
                                            <p><strong>Remarks:</strong> {mlmrequest.remarks}</p>

                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                    )}
                </div>
            </main>
            {/* Footer */}
            <footer className="bg-[#2a0f2f] border-t border-blue-800 text-blue-300 py-4">
                <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
                    <div className="text-xs lg:text-sm">
                        © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
                        <Link to="/about-us" className="text-white hover:text-blue-400">About</Link>
                        <Link to="/privacy-policy" className="text-white hover:text-blue-400">Privacy Policy</Link>
                        <Link to="/terms-of-use" className="text-white hover:text-blue-400">Terms of Use</Link>
                        <Link to="/contact-us" className="text-white hover:text-blue-400">Contact</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default OrderFlowMyMLMRequestList;